export const ROAD_THEMES = Object.freeze({
  red: { base: '#d84245', light: '#ffaaa0', dark: '#8e1720', glow: 'rgba(218,52,57,.34)' },
  blue: { base: '#3977d4', light: '#9dc8ff', dark: '#17458f', glow: 'rgba(45,105,210,.32)' },
  green: { base: '#42965e', light: '#a6dfb4', dark: '#1d6237', glow: 'rgba(54,148,86,.3)' }
});

export function drawRoadBoard(ctx, width, height) {
  const base = ctx.createLinearGradient(0, 0, width, height);
  base.addColorStop(0, '#fbfdf9');
  base.addColorStop(.46, '#eff4ef');
  base.addColorStop(1, '#e4ece8');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, width, height);
  const sheen = ctx.createRadialGradient(width * .34, height * .08, 0, width * .34, height * .08, width * .7);
  sheen.addColorStop(0, 'rgba(255,255,255,.62)');
  sheen.addColorStop(.52, 'rgba(255,255,255,.08)');
  sheen.addColorStop(1, 'rgba(90,125,112,.07)');
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, width, height);
}

export function drawRoadGridLine(ctx, x1, y1, x2, y2, major = false) {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineWidth = major ? 1.25 : .8;
  ctx.strokeStyle = major ? 'rgba(67,88,78,.34)' : 'rgba(79,102,92,.22)';
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x1 + (y1 === y2 ? 0 : 1), y1 + (x1 === x2 ? 0 : 1));
  ctx.lineTo(x2 + (y1 === y2 ? 0 : 1), y2 + (x1 === x2 ? 0 : 1));
  ctx.lineWidth = .65;
  ctx.strokeStyle = 'rgba(255,255,255,.58)';
  ctx.stroke();
  ctx.restore();
}

export function drawCenteredRoadText(ctx, text, centerX, centerY) {
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const metrics = ctx.measureText(String(text));
  const left = Number(metrics.actualBoundingBoxLeft) || 0;
  const right = Number(metrics.actualBoundingBoxRight) || metrics.width || 0;
  const ascent = Number(metrics.actualBoundingBoxAscent) || 0;
  const descent = Number(metrics.actualBoundingBoxDescent) || 0;
  const drawX = centerX + ((left - right) / 2);
  const drawY = centerY + ((ascent - descent) / 2);
  ctx.fillText(String(text), drawX, drawY);
}

function glassGradient(ctx, x, y, radius, theme) {
  const gradient = ctx.createRadialGradient(x - radius * .38, y - radius * .42, radius * .08, x, y, radius * 1.08);
  gradient.addColorStop(0, '#fff');
  gradient.addColorStop(.12, theme.light);
  gradient.addColorStop(.48, theme.base);
  gradient.addColorStop(.82, theme.dark);
  gradient.addColorStop(1, theme.base);
  return gradient;
}

export function drawGlassBead(ctx, x, y, radius, theme, label = '') {
  ctx.save();
  ctx.shadowColor = theme.glow;
  ctx.shadowBlur = Math.max(4, radius * .42);
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = glassGradient(ctx, x, y, radius, theme);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.lineWidth = Math.max(1.2, radius * .07);
  ctx.strokeStyle = 'rgba(255,255,255,.68)';
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x - radius * .18, y - radius * .22, radius * .62, Math.PI * 1.08, Math.PI * 1.75);
  ctx.lineWidth = Math.max(1, radius * .09);
  ctx.strokeStyle = 'rgba(255,255,255,.5)';
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y + radius * .06, radius * .82, .12 * Math.PI, .88 * Math.PI);
  ctx.lineWidth = Math.max(1, radius * .08);
  ctx.strokeStyle = 'rgba(20,25,22,.2)';
  ctx.stroke();
  if (label) {
    ctx.shadowColor = 'rgba(0,0,0,.45)';
    ctx.shadowBlur = 3;
    ctx.fillStyle = '#fffdf5';
    ctx.font = `600 ${Math.round(radius * 1.08)}px "Noto Serif SC",serif`;
    drawCenteredRoadText(ctx, label, x, y);
  }
  ctx.restore();
}

export function drawGlassRing(ctx, x, y, radius, theme, lineWidth = 2) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.lineWidth = lineWidth + 2.2;
  ctx.strokeStyle = theme.glow;
  ctx.shadowColor = theme.glow;
  ctx.shadowBlur = Math.max(2, radius * .32);
  ctx.stroke();
  ctx.shadowBlur = 0;
  const gradient = ctx.createLinearGradient(x - radius, y - radius, x + radius, y + radius);
  gradient.addColorStop(0, theme.light);
  gradient.addColorStop(.3, theme.base);
  gradient.addColorStop(.68, theme.dark);
  gradient.addColorStop(1, theme.light);
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = gradient;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x - radius * .1, y - radius * .12, radius * .84, Math.PI * 1.06, Math.PI * 1.72);
  ctx.lineWidth = Math.max(.7, lineWidth * .48);
  ctx.strokeStyle = 'rgba(255,255,255,.72)';
  ctx.stroke();
  ctx.restore();
}

export function drawGlassDot(ctx, x, y, radius, theme) {
  drawGlassBead(ctx, x, y, radius, theme, '');
}

export function drawGlassSlash(ctx, x1, y1, x2, y2, theme, lineWidth = 3) {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineWidth = lineWidth + 3;
  ctx.strokeStyle = theme.glow;
  ctx.shadowColor = theme.glow;
  ctx.shadowBlur = 5;
  ctx.stroke();
  ctx.shadowBlur = 0;
  const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
  gradient.addColorStop(0, theme.dark);
  gradient.addColorStop(.48, theme.base);
  gradient.addColorStop(1, theme.light);
  ctx.beginPath();ctx.moveTo(x1, y1);ctx.lineTo(x2, y2);ctx.lineWidth = lineWidth;ctx.strokeStyle = gradient;ctx.stroke();
  ctx.beginPath();ctx.moveTo(x1 + 1, y1 - 1);ctx.lineTo(x2 + 1, y2 - 1);ctx.lineWidth = Math.max(.7, lineWidth * .32);ctx.strokeStyle = 'rgba(255,255,255,.72)';ctx.stroke();
  ctx.restore();
}

export function drawTieArc(ctx, x, y, radius, lineWidth = 4) {
  const theme = ROAD_THEMES.green;
  ctx.save();
  ctx.beginPath();ctx.arc(x, y, radius, 0, Math.PI * .5);ctx.lineWidth = lineWidth + 2;ctx.strokeStyle = theme.glow;ctx.shadowColor = theme.glow;ctx.shadowBlur = 4;ctx.stroke();
  ctx.shadowBlur = 0;
  const gradient = ctx.createLinearGradient(x, y, x + radius, y + radius);gradient.addColorStop(0, theme.light);gradient.addColorStop(.45, theme.base);gradient.addColorStop(1, theme.dark);
  ctx.beginPath();ctx.arc(x, y, radius, 0, Math.PI * .5);ctx.lineWidth = lineWidth;ctx.strokeStyle = gradient;ctx.stroke();ctx.restore();
}
